/* eslint-disable operator-linebreak */
/* eslint-disable object-curly-newline */
/* eslint-disable @typescript-eslint/indent */
/* eslint-disable implicit-arrow-linebreak */
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { getParamFromUrl } from '@fff-web/fff-utilities';
import { getCartSubtotal } from '@fff-web/fff-cart';
import { getUserCartItems } from '../../../utils/Data/aceConnector';
import env from '../../../utils/env';
import type {
  PublicShopContextProps,
  FormattedProducts,
  ProductsMap,
  Product,
  CartItemsApiResponse,
  VariantCartItemTypes,
  VariantQuantity,
  VariantCadenceType,
} from './PublicShopContextTypes';
import { useSharedContext } from './SharedContext';

export const ITEM_TYPE = {
  refill: 'REFILL',
  intro: 'INTRO',
};

const PublicShopContext = createContext<PublicShopContextProps | undefined>(
  undefined,
);

type Props = {
  children: any;
};

const formatProducts = (
  products: ProductsMap,
  itemTypes: VariantCartItemTypes | undefined,
  variantQuantities: VariantQuantity,
  variantCadenceTypes: VariantCadenceType,
  variantIds: string[],
): FormattedProducts[] => {
  const getFormattedVariant = (product: Product, variantId: string | null) => {
    const { name, variants, shippingRestrictions, subscribable, brandName, color } =
    product;
    const quantity = variantQuantities?.[Number(variantId)] ?? 1;
    const variantData = variants[Number(variantId)] ?? {};
    const { price, salePrice, sku, imageUrl } = variantData;
    return {
      name: color ? `${color} ${name}` : name,
      image: imageUrl,
      price,
      salePrice,
      quantity,
      sku,
      shippingRestrictions,
      subscribable,
      type: itemTypes?.[Number(variantId)] ?? '',
      cadence: variantCadenceTypes?.[Number(variantId)] ?? '',
      brandName,
    };
  };

  return Object.entries(products).map(
    ([, product]: [string, Product]) => {
      if (variantIds.length) {
        return variantIds.filter(
          (variantId) => !!product.variants[Number(variantId)],
        ).map(
          (variantId) => getFormattedVariant(product, variantId),
        );
      }
      // Handling the rare case that a product has no variants
      return getFormattedVariant(product, null);
    },
  ).flat();
};

const PublicShopProvider = (props: Props) => {
  const { children } = props;
  const [sku, setSku] = useState('');
  const { setInitialLoading } = useSharedContext();
  // cartItems holds the whole API response, also holding cartId and the ItemTypes(Refill, Intro)
  const [cartItems, setCartItems] = useState<
    CartItemsApiResponse | Record<string, never>
  >({});
  const { cartId } = cartItems;

  const products = useMemo<FormattedProducts[]>(() => {
    if (Object.keys(cartItems).length > 0) {
      return formatProducts(
        cartItems.products,
        cartItems.cartDetails.variantCartItemTypes,
        cartItems.cartDetails.variantQuantities,
        cartItems.cartDetails.variantCadenceTypes,
        cartItems.cartDetails.variantIds,
      );
    }
    return [];
  }, [cartItems]);

  const subtotal = useMemo<string>(() => getCartSubtotal(products), [products]);

  const hasRefill = useMemo<boolean>(
    () =>
      products?.some((product) => product.type === ITEM_TYPE.refill) || false,
    [products],
  );

  const hasIntro = useMemo<boolean>(
    () =>
      products?.some((product) => product.type === ITEM_TYPE.intro) || false,
    [products],
  );

  useEffect(() => {
    const skuParam = getParamFromUrl('sku');
    setSku(skuParam);
  }, []);

  useEffect(() => {
    getUserCartItems()
      .then((response) => {
        const { status, data } = response;
        if (status === 200) {
          setCartItems(data);
          setInitialLoading(false);
        } else {
          window.location.replace(`${env.envURL}/shop`);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  }, []);

  return (
    <PublicShopContext.Provider
      value={{
        sku,
        choices: products,
        hasRefill,
        hasIntro,
        subtotal,
        cartId,
      }}
    >
      {children}
    </PublicShopContext.Provider>
  );
};

export function usePublicShopContext() {
  const context = React.useContext(PublicShopContext);
  if (!context) {
    throw new Error(
      'You must use the PublicShop Provider in order to consume this context.',
    );
  }
  return context;
}

export default PublicShopProvider;
